import { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { X, CheckSquare } from 'lucide-react';
import type { TicketStatus } from './TicketStatusBadge';
import type { TicketPriority } from './TicketPriorityBadge';

interface BulkActionsBarProps {
  selectedCount: number;
  onClearSelection: () => void;
  onBulkStatusChange: (status: string) => Promise<void>;
  onBulkPriorityChange: (priority: string) => Promise<void>;
  onBulkClose: () => Promise<void>;
}

const statusOptions: { value: TicketStatus; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'waiting_on_customer', label: 'Waiting on Customer' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const priorityOptions: { value: TicketPriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

export function BulkActionsBar({
  selectedCount,
  onClearSelection,
  onBulkStatusChange,
  onBulkPriorityChange,
  onBulkClose,
}: BulkActionsBarProps) {
  const [applying, setApplying] = useState(false);

  const handleAction = async (action: () => Promise<void>) => {
    setApplying(true);
    try {
      await action();
    } finally {
      setApplying(false);
    }
  };

  if (selectedCount === 0) return null;

  return (
    <div className="sticky bottom-4 z-10 mx-auto w-fit">
      <div className="flex items-center gap-3 rounded-lg border bg-card px-4 py-3 shadow-lg">
        <div className="flex items-center gap-2 text-sm font-medium">
          <CheckSquare className="h-4 w-4 text-primary" />
          {selectedCount} ticket{selectedCount !== 1 ? 's' : ''} selected
        </div>

        <div className="h-5 w-px bg-border" />

        <Select
          onValueChange={(value) => handleAction(() => onBulkStatusChange(value))}
          disabled={applying}
        >
          <SelectTrigger className="h-8 w-[150px] text-xs">
            <SelectValue placeholder="Set Status..." />
          </SelectTrigger>
          <SelectContent>
            {statusOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          onValueChange={(value) => handleAction(() => onBulkPriorityChange(value))}
          disabled={applying}
        >
          <SelectTrigger className="h-8 w-[140px] text-xs">
            <SelectValue placeholder="Set Priority..." />
          </SelectTrigger>
          <SelectContent>
            {priorityOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant="destructive"
          size="sm"
          className="h-8 text-xs"
          onClick={() => handleAction(onBulkClose)}
          disabled={applying}
        >
          Close All
        </Button>

        <div className="h-5 w-px bg-border" />

        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-xs"
          onClick={onClearSelection}
          disabled={applying}
        >
          <X className="h-3 w-3 mr-1" />
          Clear
        </Button>
      </div>
    </div>
  );
}
